/* =====================================================================
   Envio de lead/conversão. O simulador registra a conversão em DUAS
   etapas (exigência Stellantis): ao concluir os dados pessoais (etapa 2)
   e ao simular/enviar a proposta (etapa 5) — assim, se o visitante
   abandonar, a concessionária ainda fica com o contato. O campo `channel`
   diferencia as duas conversões no CRM (evita a leitura de "lead duplicado").

   Destino: crm-api `/conversions` (API pública de leads, mesma que os
   formulários usam via `NEXT_PUBLIC_DEFAULT_FORM_SUBMIT_URL`). O antibot é
   fail-open no crm-api, então a conversão é aceita mesmo sem token.
   A URL é inlined pelo Next no renderer; no builder (Vite) fica vazia e o
   envio é ignorado (só o CustomEvent dispara).
   ===================================================================== */

import {
  captureLeadAttempt,
  createCaptureId,
  outcomeForStatus,
  reportCaptureOutcome,
} from '../../../utils/leadCapture';

export type LeadStage = 'personal' | 'proposal';

const CAPTURE_SOURCE = 'SimuladorFinanciamento';

const REQUIRED_FIELDS = ['accountId', 'siteId', 'name', 'email', 'phone'] as const;

export const LEAD_CHANNEL: Record<LeadStage, string> = {
  personal: 'Simulador de Financiamento - Dados pessoais',
  proposal: 'Simulador de Financiamento - Simulação',
};

export interface ConversionPayload {
  accountId?: string;
  siteId?: string;
  name: string;
  email: string;
  phone: string;
  product: string;
  channel: string;
  unitId?: string;
  visitorId?: string;
  // Antibot: sem token o crm-api bloqueia a conversão (403) onde está ativo.
  antiBotToken?: string;
  antiBotAction?: string;
  // campos extras (viram customParameters no crm-api via catchall)
  [key: string]: unknown;
}

const missingRequiredField = (payload: ConversionPayload): string | undefined =>
  REQUIRED_FIELDS.find((field) => !payload[field]);

const capturablePayload = ({
  antiBotToken,
  antiBotAction,
  ...rest
}: ConversionPayload): Record<string, unknown> => rest;

const getSubmitUrl = (): string => {
  try {
    return (process.env.NEXT_PUBLIC_DEFAULT_FORM_SUBMIT_URL as string | undefined) || '';
  } catch {
    return '';
  }
};

export const captureLeadAttemptFor = (
  payload: ConversionPayload,
  stage: LeadStage,
): string => {
  const captureId = createCaptureId();

  captureLeadAttempt({
    captureId,
    source: CAPTURE_SOURCE,
    rawPayload: capturablePayload(payload),
    pageUrl: typeof window !== 'undefined' ? window.location.href : undefined,
    formId: stage,
    visitorId: payload.visitorId,
  });

  return captureId;
};

/** POST não-bloqueante da conversão. Ignora quando falta URL (builder) ou
    dados obrigatórios do crm-api (accountId/siteId/name/email/phone). */
export const submitConversion = async (
  payload: ConversionPayload,
  captureId: string,
): Promise<void> => {
  const url = getSubmitUrl();
  if (!url) return;

  const missingField = missingRequiredField(payload);

  if (missingField) {
    reportCaptureOutcome({
      captureId,
      outcome: 'REJECTED_CLIENT_VALIDATION',
      outcomeDetail: `Campo obrigatório ausente: ${missingField}`,
    });
    return;
  }

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...payload, af_capture_id: captureId }),
    });

    reportCaptureOutcome(
      response.ok
        ? { captureId, outcome: 'ACCEPTED_QUEUED' }
        : { captureId, ...outcomeForStatus(response.status) },
    );
  } catch (error) {
    /* envio de lead é non-blocking — não trava o fluxo do visitante */
    reportCaptureOutcome({
      captureId,
      outcome: 'TRANSPORT_FAILED',
      outcomeDetail: error instanceof Error ? error.message : undefined,
    });
  }
};
